import React, { useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

import { useDraggable } from '../../../widgets';
import { Z_DIALOG } from '../../../constants/zLayers';
import NoteHoverPopover from './NoteHoverPopover';

/**
 * ProcedureChip — compact one-row representation of a Sitting inside the
 * Phase 3.0 Procedure half-disc widget.
 *
 * Matches SittingCard's status colour map (Tailwind classes) so the look
 * stays consistent with the queue page. Renders three lines:
 *   1) status pill + procedure name + cost
 *   2) when (today / day-N) + assignee name
 *   3) VisibilityRibbon — "→ in {assignee}'s queue {when}"
 *
 * Props:
 *   sitting       — SittingSummary or SittingResponse (same shape as SittingCard)
 *   primaryAction — optional { key, label } shown as the right-side chip button
 *   onAction      — async ({ action, sitting }) => void
 *   onDelete      — optional () => void; renders ✕ button when supplied
 *   compact       — bool, hides cost+ribbon for the 80px shrunk disc state
 */

const STATUS_COLOR = {
  awaiting_payment:  { bg: 'bg-amber-100',   text: 'text-amber-900',   border: 'border-amber-300',
                       darkBg: 'dark:bg-amber-900/40',   darkText: 'dark:text-amber-200',   darkBorder: 'dark:border-amber-700' },
  payment_cleared:   { bg: 'bg-teal-100',    text: 'text-teal-900',    border: 'border-teal-300',
                       darkBg: 'dark:bg-teal-900/40',    darkText: 'dark:text-teal-200',    darkBorder: 'dark:border-teal-700' },
  // Phase 3 — `held` is the muted "waiting on prior step" state. Solid
  // slate so day-mode contrast survives next to teal/blue siblings.
  held:              { bg: 'bg-slate-100',   text: 'text-slate-700',   border: 'border-slate-300',
                       darkBg: 'dark:bg-slate-800',     darkText: 'dark:text-slate-300',   darkBorder: 'dark:border-slate-600' },
  // Phase 3 — `ready` is post-promotion executable; visually identical
  // to payment_cleared because the assignee's action (Start) is the
  // same.
  ready:             { bg: 'bg-teal-100',    text: 'text-teal-900',    border: 'border-teal-300',
                       darkBg: 'dark:bg-teal-900/40',    darkText: 'dark:text-teal-200',    darkBorder: 'dark:border-teal-700' },
  scheduled:         { bg: 'bg-sky-100',     text: 'text-sky-900',     border: 'border-sky-300',
                       darkBg: 'dark:bg-sky-900/40',     darkText: 'dark:text-sky-200',     darkBorder: 'dark:border-sky-700' },
  in_progress:       { bg: 'bg-blue-100',    text: 'text-blue-900',    border: 'border-blue-300',
                       darkBg: 'dark:bg-blue-900/40',    darkText: 'dark:text-blue-200',    darkBorder: 'dark:border-blue-700' },
  pending_approval:  { bg: 'bg-purple-100',  text: 'text-purple-900',  border: 'border-purple-300',
                       darkBg: 'dark:bg-purple-900/40',  darkText: 'dark:text-purple-200',  darkBorder: 'dark:border-purple-700' },
  completed:         { bg: 'bg-emerald-100', text: 'text-emerald-900', border: 'border-emerald-300',
                       darkBg: 'dark:bg-emerald-900/40', darkText: 'dark:text-emerald-200', darkBorder: 'dark:border-emerald-700' },
  paid:              { bg: 'bg-emerald-100', text: 'text-emerald-900', border: 'border-emerald-300',
                       darkBg: 'dark:bg-emerald-900/40', darkText: 'dark:text-emerald-200', darkBorder: 'dark:border-emerald-700' },
  cancelled:         { bg: 'bg-slate-100',   text: 'text-slate-600',   border: 'border-slate-300',
                       darkBg: 'dark:bg-slate-800',     darkText: 'dark:text-slate-400',  darkBorder: 'dark:border-slate-600' },
  needs_reassignment:{ bg: 'bg-rose-100',    text: 'text-rose-900',    border: 'border-rose-300',
                       darkBg: 'dark:bg-rose-900/40',    darkText: 'dark:text-rose-200',    darkBorder: 'dark:border-rose-700' },
};

const STATUS_ICON = {
  awaiting_payment:  '💳',
  payment_cleared:   '▶',
  held:              '⏸',
  ready:             '▶',
  scheduled:         '⏳',
  in_progress:       '⏵',
  pending_approval:  '👁',
  completed:         '✓',
  paid:              '✓',
  cancelled:         '✕',
  needs_reassignment:'⚠',
};

// Solid accent hex per status — drives the card's left rail + status-pill
// dot in the redesigned (non-compact) chip. Saturated enough to read in
// both light and dark, so one value covers both modes.
const STATUS_ACCENT = {
  awaiting_payment:  '#d97706',
  payment_cleared:   '#0d9488',
  ready:             '#0d9488',
  held:              '#64748b',
  scheduled:         '#0ea5e9',
  in_progress:       '#2563eb',
  pending_approval:  '#9333ea',
  completed:         '#059669',
  paid:              '#059669',
  cancelled:         '#94a3b8',
  needs_reassignment:'#e11d48',
};

// Short human label for the status pill (the location text lives in
// describeQueue; this is the at-a-glance state).
const STATUS_LABEL = {
  awaiting_payment:  'Awaiting payment',
  payment_cleared:   'Ready',
  ready:             'Ready',
  held:              'Waiting',
  scheduled:         'Scheduled',
  in_progress:       'In progress',
  pending_approval:  'Awaiting approval',
  completed:         'Completed',
  paid:              'Paid',
  cancelled:         'Cancelled',
  needs_reassignment:'Needs reassignment',
};

function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === '') return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatWhen(sitting) {
  const raw = sitting?.scheduled_for;
  const timingMode = sitting?.timing_mode;
  // When there's no scheduled_for, fall back to created_at so an "added
  // now" sitting from days ago doesn't keep rendering as "today" forever.
  // Labels in this branch are prefixed "added …" so the user can tell
  // they're looking at the creation date rather than a schedule date.
  if (!raw) {
    const created = sitting?.created_at;
    if (created) {
      try {
        const [datePart] = String(created).split('T');
        const target = new Date(`${datePart}T00:00:00`);
        const today = new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00`);
        const days = Math.round((today.getTime() - target.getTime()) / 86400000);
        if (days <= 0) {
          if (timingMode === 'later_today') return 'added later today';
          return 'added today';
        }
        if (days === 1) return 'added yesterday';
        if (days <= 14) return `added ${days}d ago`;
        return `added ${target.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
      } catch {
        // fall through to timing_mode hints below
      }
    }
    if (timingMode === 'now') return 'today';
    if (timingMode === 'later_today') return 'later today';
    return null;
  }
  try {
    const [datePart] = String(raw).split('T');
    const target = new Date(`${datePart}T00:00:00`);
    const today = new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00`);
    const days = Math.round((target.getTime() - today.getTime()) / 86400000);
    if (days < 0) return `overdue · ${target.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days <= 14) return `day ${days}`;
    return target.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return null;
  }
}

function describeQueue(sitting) {
  // Maps current status to where this work currently lives, so the
  // doctor can see at a glance where it went after Save.
  switch (sitting?.status) {
    case 'awaiting_payment':  return 'action-pending tab';
    case 'payment_cleared':
    case 'ready':             return 'queue';
    case 'scheduled':         return 'queue';
    case 'held':              return 'waiting on prior step';
    case 'in_progress':       return 'in progress';
    case 'pending_approval':  return 'awaiting approval';
    case 'paid':
    case 'completed':         return 'history';
    case 'cancelled':         return 'cancelled';
    case 'needs_reassignment':return 'needs reassignment';
    default:                  return 'queue';
  }
}

function ProcedureChipImpl({
  sitting,
  primaryAction,
  secondaryAction,
  noteAction,
  onAction,
  onDelete,
  canReassign = false,
  canCancel = false,
  compact = false,
  lockReason = null,
  showPatient = false,
}) {
  const [busy, setBusy] = useState(null);
  const status = sitting?.status || 'scheduled';
  const meta = STATUS_COLOR[status] || STATUS_COLOR.scheduled;
  const icon = STATUS_ICON[status] || '•';
  const accent = STATUS_ACCENT[status] || '#94a3b8';
  const statusLabel = STATUS_LABEL[status] || status;

  const procedureName =
    sitting?.procedure_name
    || sitting?.procedure_snapshot?.name
    || '(procedure)';
  const assignedToName =
    sitting?.assigned_to_name
    || sitting?.assigned_to?.name
    || sitting?.assigned_to_member_id
    || 'Unassigned';
  const assignedByName =
    sitting?.assigned_by_name
    || sitting?.assigned_by?.name
    || null;
  const assignedToId =
    sitting?.assigned_to?.member_id
    || sitting?.assigned_to_member_id
    || null;
  const assignedById =
    sitting?.assigned_by?.member_id
    || sitting?.assigned_by_member_id
    || null;
  const visitDoctorId = sitting?.visit_doctor_member_id || null;
  // "Delegated" = the performer is someone OTHER than the visit's own doctor
  // (e.g. handed to a nurse) — the same definition SittingCard uses
  // (`isVisitDoctor && !isAssignee`). A procedure simply (re)assigned to the
  // visit's doctor — e.g. re-pointed to the new doctor at registration — is NOT
  // delegated, even though whoever did the assignment differs from the assignee.
  // Fall back to the assigner≠assignee signal only when the visit doctor isn't
  // known (legacy / future-scheduled rows not yet bound to a visit).
  const showDelegator = !!assignedToId
    && (visitDoctorId
      ? assignedToId !== visitDoctorId
      : !!assignedByName
        && (assignedById ? assignedById !== assignedToId : assignedByName !== assignedToName));
  const cost = formatCurrency(
    sitting?.final_cost
    ?? sitting?.estimated_cost
    ?? sitting?.effective_cost_snapshot,
  );
  const whenLabel = formatWhen(sitting);
  // Render the tooth/quadrant tokens this procedure applies to so the
  // doctor sees "RCT on 16" at a glance, both in today's chip-list and
  // on the next visit's history view.
  const toothTokens = Array.isArray(sitting?.tooth_numbers)
    ? sitting.tooth_numbers.filter(Boolean)
    : [];

  const fire = async (key) => {
    if (!onAction || busy) return;
    setBusy(key);
    try { await onAction({ action: key, sitting }); }
    finally { setBusy(null); }
  };

  // Phase 3 — chip is a drag source for the procedure drop flow. Drop
  // on a tooth → opens picker pre-selected with this procedure so the
  // dentist can apply the same procedure to another tooth in one drag.
  // Hook called BEFORE the early return below to satisfy rules-of-hooks.
  const dragProps = useDraggable({
    type: 'procedure',
    source: 'rail-chip',
    procedureId: sitting?.procedure_id || sitting?.procedure_snapshot?.id || null,
    procedureName,
    defaultCost: sitting?.estimated_cost ?? sitting?.effective_cost_snapshot,
    isToothSpecific: !!sitting?.procedure_snapshot?.is_tooth_specific,
  });

  if (compact) {
    // 80px shrunk mode — icon + status colour only. Click expands the disc.
    return (
      <div
        title={`${procedureName} · ${assignedToName}`}
        className={`flex items-center justify-center rounded-md border ${meta.bg} ${meta.text} ${meta.border} ${meta.darkBg} ${meta.darkText} ${meta.darkBorder}`}
        style={{ width: '100%', height: 32, fontSize: '0.875rem' }}
      >
        <span aria-hidden="true">{icon}</span>
      </div>
    );
  }

  return (
    <article
      {...(lockReason ? {} : dragProps)}
      className="relative rounded-xl border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
      style={{
        padding: '0.625rem 0.75rem 0.625rem 0.875rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.3rem',
        boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        cursor: lockReason ? 'not-allowed' : 'grab',
      }}
    >
      {/* status accent rail */}
      <span
        aria-hidden="true"
        style={{ position: 'absolute', left: 0, top: 10, bottom: 10, width: 4, borderRadius: '0 4px 4px 0', background: accent }}
      />

      <header style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        <span
          title={procedureName}
          className="text-slate-900 dark:text-slate-100"
          style={{
            fontSize: '0.875rem',
            fontWeight: 700,
            flex: 1,
            lineHeight: 1.25,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: '-0.01em',
            opacity: lockReason ? 0.7 : 1,
          }}
        >
          {procedureName}
        </span>
        {cost && (
          <span
            className="text-slate-900 dark:text-slate-100"
            style={{ fontSize: '0.8125rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          >
            {cost}
          </span>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Remove procedure"
            title="Remove"
            className="rounded-md border bg-white text-slate-500 hover:text-rose-600 hover:border-rose-300 hover:bg-rose-50
                       dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600 dark:hover:bg-rose-900/30 dark:hover:text-rose-300"
            style={{ fontSize: '0.6875rem', lineHeight: 1, padding: '0.2rem 0.4rem', flexShrink: 0 }}
          >
            ✕
          </button>
        )}
      </header>

      {/* status pill + delegated flag */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5 }}>
        <span
          className={`border ${meta.bg} ${meta.text} ${meta.border} ${meta.darkBg} ${meta.darkText} ${meta.darkBorder}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.6875rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: 9999, lineHeight: 1.3 }}
        >
          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: accent }} />
          {lockReason ? 'Waiting' : statusLabel}
        </span>
        {showDelegator && (
          <span
            className="border bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-700"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.6875rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: 9999, lineHeight: 1.3 }}
            title={`Delegated to ${assignedToName}`}
          >
            Delegated
          </span>
        )}
        {sitting.standing && (
          <span
            className="border bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 dark:border-fuchsia-700"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.6875rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: 9999, lineHeight: 1.3 }}
            title={sitting.expires_at ? `Standing order — repeats until ${sitting.expires_at}` : 'Standing (repeatable) order'}
          >
            Standing{sitting.expires_at ? ` · till ${sitting.expires_at}` : ''}
          </span>
        )}
      </div>

      {/* Tooth tokens this sitting applies to. */}
      {toothTokens.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            alignItems: 'center',
            fontSize: '0.6875rem',
            opacity: 0.85,
          }}
          title={`Applied to: ${toothTokens.join(', ')}`}
        >
          <span aria-hidden="true">🦷</span>
          {toothTokens.slice(0, 6).map((t) => (
            <span
              key={t}
              className="border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
              style={{
                padding: '0.05rem 0.4rem',
                borderRadius: 9999,
                border: '1px solid',
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {t.startsWith('gum:') ? `🩸${t.slice(4)}` : t}
            </span>
          ))}
          {toothTokens.length > 6 && (
            <span style={{ opacity: 0.65 }}>
              +{toothTokens.length - 6}
            </span>
          )}
        </div>
      )}

      {showPatient && (sitting?.patient_name || sitting?.queue_number != null) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            // Phase 4.0j — bumped from 0.75rem and weight 600 so the
            // patient name is the most-readable element on the chip.
            // Two near-identical names ("Ram", "Ramesh") need to read as
            // distinct at a glance to back up the Start confirmation
            // modal.
            fontSize: '0.875rem',
            fontWeight: 700,
            minWidth: 0,
            lineHeight: 1.2,
          }}
        >
          {sitting?.queue_number != null && (
            <span
              className="bg-teal-600 text-white"
              style={{
                fontSize: '0.625rem',
                fontWeight: 700,
                padding: '0.05rem 0.4rem',
                borderRadius: 9999,
                flexShrink: 0,
                letterSpacing: '0.04em',
              }}
            >
              Q-{sitting.queue_number}
            </span>
          )}
          {sitting?.patient_name && (
            <span
              title={sitting.patient_name}
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {sitting.patient_name}
            </span>
          )}
        </div>
      )}

      <div className="text-slate-500 dark:text-slate-400" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.6875rem', flexWrap: 'wrap' }}>
        {whenLabel && <span>{whenLabel}</span>}
        {whenLabel && <span aria-hidden="true">·</span>}
        <span className="text-slate-600 dark:text-slate-300" style={{ fontWeight: 600 }}>👤 {assignedToName}</span>
        {showDelegator && (
          <>
            <span aria-hidden="true">·</span>
            <span title={`Assigned by ${assignedByName}`}>
              from {assignedByName}
            </span>
          </>
        )}
      </div>

      {/* VisibilityRibbon — closes the "where did it go?" gap. */}
      <p className="text-slate-400 dark:text-slate-500" style={{ margin: 0, fontSize: '0.6875rem', lineHeight: 1.3 }}>
        → in {assignedToName}'s {describeQueue(sitting)}
      </p>

      {/* Strict-ordering lock hint — the start button is hidden by the
          chip list while this is set, so the user knows why. */}
      {lockReason && (
        <p
          style={{
            margin: 0,
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: '#9a3412',
            lineHeight: 1.3,
          }}
        >
          🔒 {lockReason}
        </p>
      )}

      {(primaryAction || secondaryAction || noteAction || canReassign || canCancel) && (
        <div style={{ marginTop: '0.3rem', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {primaryAction && (
            <button
              type="button"
              onClick={() => fire(primaryAction.key)}
              disabled={busy === primaryAction.key || !!primaryAction.disabled}
              title={primaryAction.note || undefined}
              className="rounded-lg text-white text-xs font-bold disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                padding: '0.4rem 0.85rem',
                background: 'linear-gradient(135deg,#0d9488,#14b8a6)',
                boxShadow: '0 6px 14px -8px rgba(13,148,136,0.85)',
              }}
            >
              {busy === primaryAction.key ? 'Working…' : primaryAction.label}
            </button>
          )}
          {noteAction && (
            <NoteHoverPopover sittingId={sitting?.sitting_id}>
              <button
                type="button"
                onClick={() => fire(noteAction.key)}
                disabled={busy === noteAction.key}
                title="Hover to preview · click to view all"
                className="rounded-lg border bg-white text-slate-700 text-xs font-semibold
                           hover:bg-slate-100
                           dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600
                           disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ padding: '0.4rem 0.7rem' }}
              >
                {busy === noteAction.key ? '…' : (noteAction.label || '📝 Notes')}
              </button>
            </NoteHoverPopover>
          )}
          {secondaryAction && (
            <button
              type="button"
              onClick={() => fire(secondaryAction.key)}
              disabled={busy === secondaryAction.key}
              className="rounded-lg border border-rose-300 bg-white
                         text-rose-700 text-xs font-semibold hover:bg-rose-50
                         disabled:opacity-60 disabled:cursor-not-allowed
                         dark:bg-slate-800 dark:text-rose-200 dark:border-rose-700
                         dark:hover:bg-rose-900/30"
              style={{ padding: '0.4rem 0.7rem' }}
            >
              {busy === secondaryAction.key ? 'Working…' : secondaryAction.label}
            </button>
          )}
          {(canReassign || canCancel) && (
            <ProcedureChipOverflowMenu
              canReassign={canReassign}
              canCancel={canCancel}
              onReassign={() => onAction && onAction({ action: 'reassign', sitting })}
              onCancel={() => onAction && onAction({ action: 'cancel', sitting })}
            />
          )}
        </div>
      )}
      {/* Doctor-collect gated note — patient must pay at Reception first. */}
      {primaryAction?.disabled && primaryAction?.note && (
        <p style={{
          marginTop: '0.3rem', fontSize: '0.6875rem', lineHeight: 1.3,
          color: '#b45309',
        }}>
          {primaryAction.note}
        </p>
      )}
    </article>
  );
}


/**
 * Tiny ⋯ overflow menu — opens on click, closes on outside-click or
 * Escape. Renders the role-gated chip actions (Reassign / Cancel)
 * that historically only lived on SittingCard. Keeps the chip itself
 * click-stable (no hover-driven panels).
 */
const OVERFLOW_MENU_WIDTH = 170;

function ProcedureChipOverflowMenu({
  canReassign, canCancel, onReassign, onCancel,
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = React.useRef(null);
  const menuRef = React.useRef(null);
  // Fixed-coordinate placement so the menu escapes the panel's
  // `overflow:hidden` clip (the panel scrolls; an absolutely-positioned
  // dropdown would be cut off at the bottom edge — that was the bug).
  const [coords, setCoords] = useState(null);

  // Measure the trigger and decide up/down flip on open + layout.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    const place = () => {
      const r = buttonRef.current?.getBoundingClientRect();
      if (!r) return;
      // ~46px per item + 12px padding; two items max.
      const itemCount = (canReassign ? 1 : 0) + (canCancel ? 1 : 0);
      const menuHeight = itemCount * 46 + 12;
      const spaceBelow = window.innerHeight - r.bottom;
      const flipUp = spaceBelow < menuHeight + 12 && r.top > menuHeight + 12;
      setCoords({
        left: Math.max(8, r.right - OVERFLOW_MENU_WIDTH),
        top: flipUp ? r.top - menuHeight - 6 : r.bottom + 6,
      });
    };
    place();
    // Reposition while open if the panel scrolls or the window resizes.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, canReassign, canCancel]);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleDoc = (e) => {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inButton && !inMenu) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleDoc);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDoc);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="More actions"
        className="rounded-lg border bg-white text-slate-600 text-xs font-semibold
                   hover:bg-slate-100
                   dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:border-slate-600"
        style={{ padding: '0.4rem 0.6rem', lineHeight: 1 }}
      >
        ⋯
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          role="menu"
          // Portaled to <body>, so it sits OUTSIDE the RoleTaskBadge DOM.
          // Without this marker, RoleTaskBadge's click-outside handler
          // collapses the bubble on the menu-item mouseup and unmounts the
          // menu before its click fires — reassign/cancel then no-op.
          data-keep-bubble-open="1"
          style={{
            position: 'fixed', left: coords.left, top: coords.top,
            width: OVERFLOW_MENU_WIDTH, zIndex: Z_DIALOG, padding: 6,
            boxShadow: '0 18px 40px -12px rgba(16,24,40,0.35), 0 6px 16px -8px rgba(16,24,40,0.2)',
          }}
          className="rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-600 text-xs"
        >
          {canReassign && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onReassign && onReassign(); }}
              className="w-full text-left rounded-lg px-3 py-2 font-semibold text-slate-700 dark:text-slate-100
                         hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              🔁 Reassign…
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onCancel && onCancel(); }}
              className="w-full text-left rounded-lg px-3 py-2 font-semibold text-rose-700 dark:text-rose-300
                         hover:bg-rose-50 dark:hover:bg-rose-900/30"
            >
              ✕ Cancel procedure
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}


// Memoize the chip so a state change in the parent (filter, tab
// toggle, hover-driven CSS state) only re-renders chips whose props
// actually changed. Combined with stable callback identity in
// ProcedureChipList, single-chip clicks no longer cause every other
// chip to re-render.
export default React.memo(ProcedureChipImpl);
